import { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { fetchPublicRulebook } from "../../lib/ligamanagerApi";

function prettyDate(value) {
    if (!value) return "—";

    let raw = String(value).trim();

    /*
        Google liefert meistens mit Z oder Offset:
        2026-06-16T14:30:00.000Z
        2026-06-16T14:30:00+00:00

        MySQL/FastAPI liefert bei dir offenbar ohne Zeitzone:
        2026-06-16T14:30:00

        In dem Fall behandeln wir den Wert als UTC und hängen Z an.
    */
    const hasTimezone =
        /z$/i.test(raw) ||
        /[+-]\d{2}:\d{2}$/.test(raw);

    if (!hasTimezone && /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(raw)) {
        raw = raw.replace(" ", "T") + "Z";
    }

    const date = new Date(raw);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleString("de-DE", {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function normalizeError(err) {
    if (!err) return "Unbekannter Fehler";
    if (err instanceof Error) return err.message || "Fehler";
    return String(err);
}

function getRuleDepthFromLabel(label) {
    const cleaned = String(label || "").trim().replace(/\.$/, "");
    if (!cleaned) return 0;

    return Math.min(cleaned.split(".").length, 6);
}

export default function Rules() {
    const [rulebook, setRulebook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const contentRef = useRef(null);

    useEffect(() => {
        let active = true;

        async function loadRulebook() {
            setLoading(true);
            setErr("");

            try {
                const data = await fetchPublicRulebook("gt3x");

                if (!active) return;

                setRulebook(data?.rulebook || null);
            } catch (e) {
                if (!active) return;

                setErr(normalizeError(e));
                setRulebook(null);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }

        loadRulebook();

        return () => {
            active = false;
        };
    }, []);

    const sanitizedHtml = useMemo(() => {
        const raw = String(rulebook?.content_html || "");

        if (!raw) return "";

        return DOMPurify.sanitize(raw, {
            USE_PROFILES: { html: true },
            ADD_ATTR: [
                "target",
                "rel",
                "colspan",
                "rowspan",
                "id",
                "name",
                "class",
            ],
        });
    }, [rulebook?.content_html]);

    useEffect(() => {
        if (!sanitizedHtml || !contentRef.current) return undefined;

        const root = contentRef.current;

        /*
            Erkennt:
            3. Überschrift
            2.1 Abschnitt
            2.1.1 Unterpunkt
            2.1.1.1 Detailpunkt
        */
        const numberedLineRe = /^\s*((?:\d+(?:\.\d+){1,5})\.?|\d+\.)(?:\s+|$)/;
        const headingRe = /^\s*((?:\d+(?:\.\d+){1,5})\.?|\d+\.)(?:\s+|$)/;

        const isTocOnlyElement = (el) => {
            if (!(el instanceof HTMLElement)) return false;

            const text = (el.textContent || "").trim();
            if (!text) return false;

            const links = Array.from(el.querySelectorAll("a[href]"));
            if (!links.length) return false;

            const hasInternalLink = links.some((a) => {
                const href = String(a.getAttribute("href") || "");
                return href.startsWith("#") || href.includes("#heading=");
            });

            if (!hasInternalLink) return false;

            /*
                Inhaltsverzeichnis-Einträge sind meist:
                1 Series Overview
                2 Anmeldung
                3 Fahrzeugwahl...
                oder:
                3. Fahrzeugwahl...

                Richtige Regelpunkte wie 2.1 / 2.1.1 sollen NICHT übersprungen werden.
            */
            if (/^\s*\d+\.\d+/.test(text)) return false;

            return (
                /^\s*\d+\.?\s+\S+/.test(text) ||
                /^Änderungsverfolgung/i.test(text)
            );
        };

        const markTocItems = () => {
            root.querySelectorAll("p, div, li").forEach((el) => {
                if (!(el instanceof HTMLElement)) return;

                if (isTocOnlyElement(el)) {
                    el.classList.add("rulebook-toc-item");
                }
            });
        };

        const unwrapRulebookSubsections = () => {
            root.querySelectorAll(".rulebook-subsection").forEach((box) => {
                const parent = box.parentNode;
                if (!parent) return;

                while (box.firstChild) {
                    parent.insertBefore(box.firstChild, box);
                }

                parent.removeChild(box);
            });
        };

        const wrapLeadingNumber = (el) => {
            if (!(el instanceof HTMLElement)) return;
            if (el.dataset.rvNumberProcessed === "1") return;
            if (el.classList.contains("rulebook-subsection")) return;
            if (el.classList.contains("rulebook-toc-item")) return;
            if (isTocOnlyElement(el)) return;

            const isTableCell = el.tagName === "TD" || el.tagName === "TH";

            if (
                (el.tagName === "DIV" || isTableCell) &&
                el.querySelector("p, div, table, ul, ol, h1, h2, h3, h4, section")
            ) {
                return;
            }

            const txt = (el.textContent || "").trim();
            const match = txt.match(numberedLineRe);

            if (!match) return;

            const rawLabel = match[1];
            const label = rawLabel.replace(/\.$/, "");
            const depth = getRuleDepthFromLabel(label);

            if (!depth) return;

            let didWrap = false;

            const htmlReLoose =
                /^(\s*(?:(?:<(?:strong|b|em|i|span)[^>]*>))*\s*)((?:\d+\.)|(?:\d+(?:\.\d+){1,5}))(\s+)/i;

            if (htmlReLoose.test(el.innerHTML)) {
                el.innerHTML = el.innerHTML.replace(
                    htmlReLoose,
                    (full, before, num, space) => {
                        const cleanNum = String(num || "").replace(/\.$/, "");
                        return `${before}<span class="rv-num">${cleanNum}</span>${space}`;
                    },
                );
                didWrap = true;
            }

            if (!didWrap) {
                const walker = document.createTreeWalker(
                    el,
                    NodeFilter.SHOW_TEXT,
                    null,
                );

                const firstText = walker.nextNode();

                if (firstText) {
                    const t = firstText.nodeValue || "";
                    const m2 = t.match(
                        /^(\s*)((?:\d+\.)|(?:\d+(?:\.\d+){1,5}))(\s+)([\s\S]*)/,
                    );

                    if (m2) {
                        const [, lead, num, space, rest] = m2;
                        const cleanNum = String(num || "").replace(/\.$/, "");

                        const spanNum = document.createElement("span");
                        spanNum.className = "rv-num";
                        spanNum.textContent = cleanNum;

                        const frag = document.createDocumentFragment();

                        if (lead) {
                            frag.appendChild(document.createTextNode(lead));
                        }

                        frag.appendChild(spanNum);
                        frag.appendChild(document.createTextNode(space + rest));

                        firstText.parentNode.replaceChild(frag, firstText);
                    }
                }
            }

            el.dataset.rvRuleLabel = label;

            if (el.tagName === "TD" || el.tagName === "TH") {
                el.classList.add("rv-rule-cell");
                el.classList.add(`rv-depth-${depth}`);
            } else {
                el.classList.add("rv-rule");
                el.classList.add(`rv-depth-${depth}`);
            }

            el.dataset.rvNumberProcessed = "1";
        };


        const processHeadings = () => {
            root.querySelectorAll("h1, h2, h3, h4").forEach((h) => {
                if (!(h instanceof HTMLElement)) return;
                if (h.dataset.rvHeadingProcessed === "1") return;
                if (h.classList.contains("rulebook-toc-item")) return;
                if (isTocOnlyElement(h)) return;

                const raw = (h.textContent || "").trim();
                const match = raw.match(headingRe);

                if (!match) return;

                const label = match[1].replace(/\.$/, "");
                const depth = getRuleDepthFromLabel(label);

                if (!depth) return;

                h.classList.add("rv-heading");
                h.classList.add(`rv-depth-${depth}`);
                h.dataset.rvHeadingProcessed = "1";
            });
        };

        const normalizeRuleLabel = (value) => {
            return String(value || "")
                .trim()
                .replace(/\.$/, "");
        };

        const extractLeadingRuleLabel = (value) => {
            const text = String(value || "")
                .replace(/\s+/g, " ")
                .trim();

            /*
                Nur echte Unterpunkte:
                2.1
                2.1.2
                9.2.3
                9.2.2.1

                Keine reinen Kapitelnummern wie:
                1
                16
                P1
            */
            const match = text.match(/^((?:\d+(?:\.\d+){1,5}))\.?(?:\s+|$)/);

            return match ? normalizeRuleLabel(match[1]) : "";
        };

        const ruleAnchorId = (prefix, label) => {
            return `${prefix}-${String(label || "")
                .trim()
                .replace(/\.$/, "")
                .replace(/\./g, "-")
                .replace(/[^\w-]/g, "")}`;
        };

        const isPenaltyCatalogStart = (el) => {
            if (!(el instanceof HTMLElement)) return false;

            const text = String(el.textContent || "")
                .replace(/\s+/g, " ")
                .trim();

            if (!text || text.length > 120) return false;

            /*
                Erkennt:
                16 Anlage Strafenkatalog:
                Anlage Strafenkatalog
                Strafenkatalog

                Erkennt absichtlich NICHT:
                "unter Punkt 15 Anlage Strafenkatalog genauer definiert"
            */
            return (
                /^(?:\d+\.?\s+)?anlage\s+strafenkatalog\s*:?\s*$/i.test(text) ||
                /^strafenkatalog\s*:?\s*$/i.test(text)
            );
        };

        const ruleLabelFromElement = (el) => {
            if (!(el instanceof HTMLElement)) return "";

            const explicit = normalizeRuleLabel(el.dataset.rvRuleLabel);
            if (/^\d+(?:\.\d+){1,5}$/.test(explicit)) {
                return explicit;
            }

            const num = el.querySelector(".rv-num");
            const numText = normalizeRuleLabel(num?.textContent);
            if (/^\d+(?:\.\d+){1,5}$/.test(numText)) {
                return numText;
            }

            return extractLeadingRuleLabel(el.textContent);
        };

        const makeRuleNumberLink = (el, targetId, label, direction) => {
            if (!(el instanceof HTMLElement)) return false;

            const title =
                direction === "toPenalty"
                    ? `Zum Strafenkatalog für ${label}`
                    : `Zur Regel ${label}`;

            const className = `rv-num rv-rule-crosslink ${
                direction === "toPenalty"
                    ? "rv-rule-crosslink--to-penalty"
                    : "rv-rule-crosslink--to-rule"
            }`;

            const existingLink = el.querySelector("a.rv-rule-crosslink");
            if (existingLink instanceof HTMLAnchorElement) {
                existingLink.href = `#${targetId}`;
                existingLink.title = title;
                existingLink.className = className;
                return true;
            }

            const existingNum = el.querySelector(".rv-num");
            if (existingNum) {
                const link = document.createElement("a");
                link.className = className;
                link.href = `#${targetId}`;
                link.title = title;
                link.textContent = existingNum.textContent || label;

                existingNum.replaceWith(link);
                return true;
            }

            /*
                Fallback:
                Falls Google Docs die Nummer nicht als .rv-num erwischt hat,
                wird die führende Nummer direkt im ersten Textnode ersetzt.
            */
            const walker = document.createTreeWalker(
                el,
                NodeFilter.SHOW_TEXT,
                {
                    acceptNode(node) {
                        const parent = node.parentElement;
                        if (!parent) return NodeFilter.FILTER_REJECT;
                        if (parent.closest("a")) return NodeFilter.FILTER_REJECT;

                        const text = String(node.nodeValue || "");
                        return extractLeadingRuleLabel(text) === label
                            ? NodeFilter.FILTER_ACCEPT
                            : NodeFilter.FILTER_REJECT;
                    },
                },
            );

            const node = walker.nextNode();
            if (!node) return false;

            const raw = String(node.nodeValue || "");
            const match = raw.match(/^(\s*)((?:\d+(?:\.\d+){1,5})\.?)(\s*)/);

            if (!match) return false;

            const lead = match[1] || "";
            const numberText = normalizeRuleLabel(match[2]);
            const afterNumberSpace = match[3] || "";
            const rest = raw.slice(match[0].length);

            const link = document.createElement("a");
            link.className = className;
            link.href = `#${targetId}`;
            link.title = title;
            link.textContent = numberText;

            const frag = document.createDocumentFragment();

            if (lead) {
                frag.appendChild(document.createTextNode(lead));
            }

            frag.appendChild(link);

            if (afterNumberSpace || rest) {
                frag.appendChild(document.createTextNode(`${afterNumberSpace}${rest}`));
            }

            node.parentNode.replaceChild(frag, node);

            return true;
        };

        const decorateRuleCrosslinks = () => {
            const ruleMap = new Map();
            const penaltyMap = new Map();

            let inPenaltyCatalog = false;

            const candidates = Array.from(
                root.querySelectorAll("h1, h2, h3, h4, p, div, li, td, th"),
            );

            candidates.forEach((el) => {
                if (!(el instanceof HTMLElement)) return;
                if (el.classList.contains("rulebook-toc-item")) return;

                if (isPenaltyCatalogStart(el)) {
                    inPenaltyCatalog = true;
                    el.classList.add("rv-penalty-catalog-start");
                    return;
                }

                const label = ruleLabelFromElement(el);
                if (!label) return;

                if (inPenaltyCatalog) {
                    if (!penaltyMap.has(label)) {
                        penaltyMap.set(label, el);
                    }
                    return;
                }

                if (!ruleMap.has(label)) {
                    ruleMap.set(label, el);
                }
            });

            let linkCount = 0;

            ruleMap.forEach((ruleEl, label) => {
                const penaltyEl = penaltyMap.get(label);
                if (!penaltyEl) return;

                const ruleId = ruleAnchorId("rule", label);
                const penaltyId = ruleAnchorId("penalty", label);

                ruleEl.id = ruleId;
                penaltyEl.id = penaltyId;

                ruleEl.classList.add("rv-rule-anchor");
                penaltyEl.classList.add("rv-rule-anchor");

                const linkedRule = makeRuleNumberLink(ruleEl, penaltyId, label, "toPenalty");
                const linkedPenalty = makeRuleNumberLink(penaltyEl, ruleId, label, "toRule");

                if (linkedRule || linkedPenalty) {
                    linkCount += 1;
                }
            });

            root.dataset.rvRuleCrosslinks = String(linkCount);

            // Debug-Hilfe in der Browser-Konsole
            // eslint-disable-next-line no-console
            console.info("RaceVision Rulebook Crosslinks", {
                rules: ruleMap.size,
                penalties: penaltyMap.size,
                links: linkCount,
            });
        };

        const fixExternalLinks = () => {
            root.querySelectorAll("a[href]").forEach((a) => {
                if (!(a instanceof HTMLElement)) return;

                const href = a.getAttribute("href") || "";

                if (href.startsWith("http")) {
                    a.setAttribute("target", "_blank");
                    a.setAttribute("rel", "noreferrer");
                }
            });
        };

        const regroupRulebookSubsections = () => {
            unwrapRulebookSubsections();

            const children = Array.from(root.children);
            let currentBox = null;

            const isTopLevelBlock = (el) => {
                if (!(el instanceof HTMLElement)) return false;

                return (
                    (el.classList.contains("rv-rule") &&
                        el.classList.contains("rv-depth-1")) ||
                    (el.classList.contains("rv-heading") &&
                        el.classList.contains("rv-depth-1")) ||
                    /^H1$|^H2$/i.test(el.tagName)
                );
            };

            const isSubsectionStart = (el) => {
                if (!(el instanceof HTMLElement)) return false;

                return (
                    el.classList.contains("rv-rule") &&
                    el.classList.contains("rv-depth-2")
                );
            };

            children.forEach((node) => {
                if (!(node instanceof HTMLElement)) return;

                if (node.classList.contains("rulebook-toc-item")) {
                    currentBox = null;
                    return;
                }

                if (isTopLevelBlock(node)) {
                    currentBox = null;
                    return;
                }

                if (isSubsectionStart(node)) {
                    const box = document.createElement("section");
                    box.className = "rulebook-subsection";

                    node.parentNode.insertBefore(box, node);
                    box.appendChild(node);

                    currentBox = box;
                    return;
                }

                if (currentBox) {
                    currentBox.appendChild(node);
                }
            });
        };

        const decorateDocPills = () => {
            /*
                In Google Docs kannst du schreiben:

                [[pill:red:5 SEK.]]
                [[pill:red:1 PUNKT]]
                [[pill:yellow:1 PUNKT]]
                [[pill:blue:INFO]]
                [[pill:green:NFA]]

                Im Frontend wird daraus automatisch ein farbiges Pill.
            */
            const markerRe = /\[\[pill:(red|yellow|blue|green|neutral):([^\]]+)\]\]/gi;

            const walker = document.createTreeWalker(
                root,
                NodeFilter.SHOW_TEXT,
                {
                    acceptNode(node) {
                        const text = node.nodeValue || "";
                        if (!markerRe.test(text)) {
                            markerRe.lastIndex = 0;
                            return NodeFilter.FILTER_REJECT;
                        }

                        markerRe.lastIndex = 0;

                        const parent = node.parentElement;
                        if (!parent) return NodeFilter.FILTER_REJECT;
                        if (parent.closest(".rv-num")) return NodeFilter.FILTER_REJECT;

                        return NodeFilter.FILTER_ACCEPT;
                    },
                },
            );

            const nodes = [];
            let current = walker.nextNode();

            while (current) {
                nodes.push(current);
                current = walker.nextNode();
            }

            nodes.forEach((node) => {
                const text = node.nodeValue || "";
                const frag = document.createDocumentFragment();

                let lastIndex = 0;
                text.replace(markerRe, (full, tone, label, offset) => {
                    if (offset > lastIndex) {
                        frag.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
                    }

                    const span = document.createElement("span");
                    span.className = `rv-doc-pill rv-doc-pill--${tone}`;
                    span.textContent = label.trim();
                    frag.appendChild(span);

                    lastIndex = offset + full.length;
                    return full;
                });

                if (lastIndex < text.length) {
                    frag.appendChild(document.createTextNode(text.slice(lastIndex)));
                }

                node.parentNode.replaceChild(frag, node);
            });
        };

        const decorateDocTables = () => {
            /*
                In Google Docs kannst du in die erste Zelle einer Tabellenzeile schreiben:

                [[row:red]]
                [[row:yellow]]
                [[row:blue]]
                [[row:green]]

                Der Marker wird im Frontend entfernt und die Zeile bekommt die Farbe.
            */
            root.querySelectorAll("table tr").forEach((tr) => {
                if (!(tr instanceof HTMLTableRowElement)) return;

                const firstCell = tr.cells?.[0];
                if (!firstCell) return;

                const text = firstCell.textContent || "";
                const match = text.match(/\[\[row:(red|yellow|blue|green|neutral|penalty)\]\]/i);

                if (!match) return;

                const tone = match[1].toLowerCase() === "penalty"
                    ? "red"
                    : match[1].toLowerCase();

                tr.classList.add(`rv-table-row--${tone}`);

                firstCell.innerHTML = firstCell.innerHTML.replace(
                    /\[\[row:(red|yellow|blue|green|neutral|penalty)\]\]/i,
                    "",
                );
            });
        };

        const wrapRulebookTables = () => {
            root.querySelectorAll("table").forEach((table) => {
                if (!(table instanceof HTMLTableElement)) return;
                if (table.closest(".rulebook-table-scroll")) return;

                const wrapper = document.createElement("div");
                wrapper.className = "rulebook-table-scroll";

                table.parentNode.insertBefore(wrapper, table);
                wrapper.appendChild(table);
            });
        };

        const applyTransforms = () => {
            markTocItems();

            root.querySelectorAll("h1, h2, h3, h4, p, div, li, td, th").forEach(wrapLeadingNumber);

            processHeadings();
            fixExternalLinks();
            regroupRulebookSubsections();
            wrapRulebookTables();
            decorateDocTables();
            decorateDocPills();
            decorateRuleCrosslinks();
        };

        applyTransforms();

        const onHashChange = () => {
            setTimeout(applyTransforms, 0);
        };

        const onInternalClick = (e) => {
            const link = e.target.closest("a[href^='#']");
            if (!link) return;

            const href = String(link.getAttribute("href") || "");
            const targetId = href.startsWith("#") ? href.slice(1) : "";
            const target = targetId ? document.getElementById(targetId) : null;

            if (link.classList.contains("rv-rule-crosslink") && target) {
                e.preventDefault();

                window.history.replaceState(null, "", `#${targetId}`);

                target.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });

                target.classList.remove("rv-rule-anchor--flash");

                window.setTimeout(() => {
                    target.classList.add("rv-rule-anchor--flash");
                }, 0);

                window.setTimeout(() => {
                    target.classList.remove("rv-rule-anchor--flash");
                }, 1400);
            }

            setTimeout(applyTransforms, 0);
        };

        window.addEventListener("hashchange", onHashChange);
        root.addEventListener("click", onInternalClick);

        return () => {
            window.removeEventListener("hashchange", onHashChange);
            root.removeEventListener("click", onInternalClick);
        };
    }, [sanitizedHtml]);

    return (
        <div className="rulebook-page">
            <section className="rulebook-hero">
                <div className="rulebook-hero__inner">
                    <div>
                        <div className="rulebook-kicker">GT3 Xperience</div>

                        <h1>{rulebook?.title || "Regelwerk"}</h1>

                        <p>
                            Offizielle Regeln der GT3 Xperience.
                        </p>
                    </div>

                    <div className="rulebook-meta-grid">
                        <div className="rulebook-meta-card">
                            <span>Letzter Sync</span>
                            <strong>{prettyDate(rulebook?.last_synced_at)}</strong>
                        </div>

                        <div className="rulebook-meta-card">
                            <span>Google Änderung</span>
                            <strong>{prettyDate(rulebook?.google_modified_time)}</strong>
                        </div>

                        <div className="rulebook-meta-card">
                            <span>Version</span>
                            <strong>{rulebook?.google_version || "—"}</strong>
                        </div>
                    </div>
                </div>
            </section>

            {loading && (
                <section className="card rulebook-state-card">
                    <h2>Regelwerk wird geladen…</h2>
                    <p>
                        Die Daten kommen direkt von RaceVision und nicht mehr live aus
                        Google Docs.
                    </p>
                </section>
            )}

            {!loading && err && (
                <section className="card rulebook-state-card rulebook-state-card--error">
                    <h2>Regelwerk aktuell nicht verfügbar</h2>
                    <p>{err}</p>
                </section>
            )}

            {!loading && !err && !sanitizedHtml && (
                <section className="card rulebook-state-card rulebook-state-card--error">
                    <h2>Noch kein Regelwerk synchronisiert</h2>
                    <p>
                        Bitte im Adminbereich einmal den Regelwerk-Sync starten.
                    </p>
                </section>
            )}

            {!loading && !err && sanitizedHtml && (
                <section className="rulebook-shell">
                    <article className="card rules-card rulebook-card">
                        <div className="rulebook-card-head">
                            <div>
                                <div className="rulebook-kicker">
                                    Offizielle Version
                                </div>

                                <h2>Reglement</h2>
                            </div>

                            <span className="pill pill--int">GT3X</span>
                        </div>

                        <div
                            ref={contentRef}
                            className="rules-content rulebook-content"
                            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                        />
                    </article>
                </section>
            )}
        </div>
    );
}